import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import PropertyGallery from "./components/PropertyGallery";
import PropertyFloorPlan from "./components/PropertyFloorPlan";
import PropertySidebar from "./components/PropertySidebar";
import { useAdmin } from "../../contexts/AdminContext";

const tabs = [
  { id: "description", label: "Beschreibung" },
  { id: "plan", label: "Interaktiver Plan" },
];

export default function BienPage() {
  const [tab, setTab] = useState("description");
  const { settings } = useAdmin();

  const features = [
    { icon: "ri-ruler-2-line", value: settings.propertySurface, label: "Wohnfläche", color: "text-primary", bg: "bg-amber-50" },
    { icon: "ri-hotel-bed-line", value: settings.propertyRooms, label: "Zimmer", color: "text-sky-500", bg: "bg-sky-50" },
    { icon: "ri-shower-line", value: settings.propertyBathrooms, label: "Badezimmer", color: "text-teal-500", bg: "bg-teal-50" },
    { icon: "ri-drop-line", value: "Pool", label: "& privater Spa", color: "text-secondary", bg: "bg-emerald-50" },
    { icon: "ri-tree-line", value: settings.propertyLand, label: "Garten", color: "text-lime-600", bg: "bg-lime-50" },
    { icon: "ri-car-line", value: "Garage", label: "2 Stellplätze", color: "text-primary", bg: "bg-orange-50" },
    { icon: "ri-sun-line", value: "Südseite", label: "Ausrichtung", color: "text-yellow-500", bg: "bg-yellow-50" },
    { icon: "ri-wifi-line", value: "Glasfaser", label: "Internet 1 Gb/s", color: "text-violet-500", bg: "bg-violet-50" },
  ];

  const highlightsIcons = [
    "ri-home-4-line", "ri-leaf-line", "ri-shield-check-line", "ri-bank-line", "ri-key-line", "ri-camera-line"
  ];
  const highlightsColors = [
    "text-secondary", "text-lime-600", "text-sky-500", "text-primary", "text-primary", "text-rose-500"
  ];

  const dynamicHighlights = settings.propertyHighlights.map((text, i) => ({
    icon: highlightsIcons[i % highlightsIcons.length],
    text,
    color: highlightsColors[i % highlightsColors.length]
  }));

  return (
    <main className="min-h-screen bg-slate-50 font-inter">
      <Navbar />

      {/* Breadcrumb + Header */}
      <div className="bg-white border-b border-slate-100 pt-24 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="text-sm text-slate-400 mb-5 flex items-center gap-2">
            <Link to="/" className="hover:text-slate-700 cursor-pointer transition-colors">Startseite</Link>
            <i className="ri-arrow-right-s-line text-slate-300"></i>
            <span className="text-slate-700 font-medium">{settings.propertyTitle}</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">🏆 GRAND PRIX</span>
                <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-map-pin-line text-xs"></i></span>
                  {settings.propertyLocation}
                </span>
              </div>
              <h1 className="font-playfair text-3xl md:text-4xl font-bold text-slate-900">
                {settings.propertyTitle}
              </h1>
              <p className="text-slate-500 mt-1 text-sm">{settings.propertyLocation} · Ziehung am {settings.nextDrawDate}</p>
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="text-slate-400 text-xs uppercase tracking-wider">Geschätzter Wert</p>
              <p className="font-playfair text-4xl font-bold text-primary">{settings.lotteryValue}</p>
              <p className="text-slate-400 text-xs mt-0.5">Notarielle Bewertung · Stand 2024</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* LEFT — Main content */}
          <div className="lg:col-span-2 flex flex-col gap-8">

            {/* Gallery */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100">
              <PropertyGallery />
            </div>

            {/* Feature chips */}
            <div className="bg-white rounded-2xl p-6 border border-slate-100">
              <h2 className="font-playfair text-xl font-bold text-slate-900 mb-4">Hauptmerkmale</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {features.map((f, i) => (
                  <div key={i} className={`${f.bg} rounded-xl p-3.5 flex flex-col items-center text-center gap-1.5`}>
                    <div className="w-9 h-9 flex items-center justify-center">
                      <i className={`${f.icon} text-xl ${f.color}`}></i>
                    </div>
                    <p className={`font-bold text-sm leading-tight ${f.color}`}>{f.value}</p>
                    <p className="text-slate-500 text-[10px] leading-tight">{f.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tabs section */}
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              {/* Tab bar */}
              <div className="flex border-b border-slate-100">
                {tabs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`flex-1 py-4 text-sm font-semibold cursor-pointer whitespace-nowrap transition-all duration-200 ${
                      tab === t.id
                        ? "text-slate-900 border-b-2 border-slate-900 bg-white"
                        : "text-slate-400 hover:text-slate-600 bg-slate-50/50"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* Description */}
                {tab === "description" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-playfair text-xl font-bold text-slate-900 mb-3">{settings.propertyTitle}</h3>
                      <div className="text-slate-600 text-sm leading-relaxed space-y-3 whitespace-pre-wrap">
                        {settings.propertyDescription}
                      </div>
                    </div>

                    <div>
                       <h3 className="font-playfair text-lg font-bold text-slate-900 mb-3">Highlights</h3>
                       <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                         {dynamicHighlights.map((h, i) => (
                           <div key={i} className="flex items-center gap-2.5 bg-slate-50 rounded-xl px-4 py-2.5">
                             <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                               <i className={`${h.icon} ${h.color} text-base`}></i>
                             </span>
                             <span className="text-slate-700 text-xs leading-tight">{h.text}</span>
                           </div>
                         ))}
                       </div>
                     </div>

                    <div className="bg-orange-50 rounded-2xl p-5 border border-orange-100">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <i className="ri-information-line text-primary text-lg"></i>
                        </div>
                        <div>
                          <p className="font-bold text-orange-800 text-sm mb-1">Bedingungen der Preisübergabe</p>
                          <p className="text-orange-700 text-xs leading-relaxed">
                            Die Villa wird dem Gewinner <strong>frei von Bewohnern</strong>, schlüsselfertig, innerhalb von 30 Tagen nach der Ziehung übergeben. Alle Notargebühren im Zusammenhang mit der Eigentumsübertragung werden vom Veranstalter übernommen.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Floor plan */}
                {tab === "plan" && <PropertyFloorPlan />}
              </div>
            </div>
          </div>

          {/* RIGHT — Sidebar */}
          <div className="lg:col-span-1">
            <PropertySidebar />
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
